import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { CheckCircle2, Package, Truck, Loader2, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import api, { resolveImage, formatPrice } from "@/lib/api";
import { STATUS_LABELS, STATUS_COLORS } from "@/lib/orderStatus";

export default function OrderConfirmation() {
  const { id } = useParams();
  const location = useLocation();
  const justPlaced = location.state?.justPlaced;
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="py-24 text-center text-slate-500">Comandă inexistentă.</div>;
  if (!order) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8" data-testid="order-confirmation-page">
      {justPlaced && (
        <div className="mb-8 flex flex-col items-center text-center" data-testid="order-thank-you">
          <CheckCircle2 className="h-14 w-14 text-success" />
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-900">Mulțumim pentru comandă!</h1>
          <p className="mt-2 text-slate-500">Comanda ta a fost plasată cu succes. Vă va contacta curierul pentru livrare.</p>
          <p className="mt-4 text-base font-bold text-red-600" data-testid="order-pickup-note">* Vă rugăm ridicați comanda când ajunge *</p>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400">Număr comandă</p>
            <p className="font-mono text-lg font-bold text-slate-900">#{order.order_number}</p>
          </div>
          <Badge className={STATUS_COLORS[order.status]}>{STATUS_LABELS[order.status] || order.status}</Badge>
        </div>

        <Separator className="my-6" />

        <div className="space-y-4">
          {order.items.map((i) => (
            <div key={i.product_id} className="flex items-center gap-4">
              <img src={resolveImage(i.image)} alt={i.name} className="h-14 w-14 rounded border border-slate-200 object-contain p-1" />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-800">{i.name}</p>
                <p className="text-xs text-slate-500">{i.qty} × {formatPrice(i.price)}</p>
              </div>
              <span className="text-sm font-semibold">{formatPrice(i.price * i.qty)}</span>
            </div>
          ))}
        </div>

        <Separator className="my-6" />

        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>{formatPrice(order.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">Transport</span><span className="font-semibold text-success">GRATUIT</span></div>
          <div className="flex justify-between text-base"><span className="font-semibold">Total</span><span className="font-heading font-bold text-primary">{formatPrice(order.total)}</span></div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg bg-slate-50 p-4 text-sm">
            <p className="flex items-center gap-2 font-medium text-slate-900"><Truck className="h-4 w-4 text-primary" /> Livrare</p>
            <p className="mt-2 text-slate-600">{order.customer.name}</p>
            <p className="text-slate-600">{order.customer.address}, {order.customer.city}, {order.customer.county}</p>
            <p className="text-slate-600">{order.customer.phone}</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-4 text-sm">
            <p className="flex items-center gap-2 font-medium text-slate-900"><Banknote className="h-4 w-4 text-primary" /> Plată</p>
            <p className="mt-2 text-slate-600">Ramburs la livrare</p>
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-center gap-3">
        <Button asChild variant="outline" className="rounded-full"><Link to="/cont">Comenzile mele</Link></Button>
        <Button asChild className="rounded-full"><Link to="/magazin">Continuă cumpărăturile</Link></Button>
      </div>
    </div>
  );
}
